import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Inbox,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
  UserPlus,
  UsersRound,
  X,
} from "lucide-react";
import { toast } from "sonner";

import TeamDropdown from "../Utils/TeamDropdown";
import useDriverTeams from "../../Sevices/Driver Api/useDriverTeams";

const getAdminId = (admin) => {
  if (!admin || typeof admin !== "object") return "";

  const id =
    admin.pk_admin_id ??
    admin.admin_id ??
    admin.adminId ??
    admin.id ??
    admin.admin_loginid ??
    admin.userId;

  return id === null || id === undefined ? "" : String(id).trim();
};

const getAdminName = (admin) => {
  const firstName = admin?.first_name || admin?.firstName || "";
  const lastName = admin?.last_name || admin?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();

  return admin?.admin_username || fullName || "Unnamed Admin";
};

const getAdminLoginId = (admin) => {
  return String(admin?.admin_loginid || admin?.userId || "");
};

const getMappingAdminId = (item) => {
  const value =
    item?.adminId ??
    item?.admin_id ??
    item?.fk_admin_id ??
    item?.admin?.adminId ??
    item?.admin?.admin_id ??
    item?.admin?.id ??
    item?.userId ??
    item?.userid ??
    item?.memberId;

  return value === null || value === undefined ? "" : String(value).trim();
};

const getMappingTeamId = (item) => {
  const value =
    item?.teamId ??
    item?.team_id ??
    item?.fk_team_id ??
    item?.team?.teamId ??
    item?.team?.team_id ??
    item?.team?.id;

  return value === null || value === undefined ? "" : String(value).trim();
};

const getTeamName = (item) => {
  return (
    item?.teamName ||
    item?.team_name ||
    item?.team?.teamName ||
    item?.team?.team_name ||
    item?.team?.name ||
    ""
  );
};

const flattenAdminTeams = (items = []) => {
  const result = [];

  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item || typeof item !== "object") return;

    const nestedAdmins =
      (Array.isArray(item?.admins) && item.admins) ||
      (Array.isArray(item?.members) && item.members) ||
      (Array.isArray(item?.teamMembers) && item.teamMembers) ||
      (Array.isArray(item?.adminTeams) && item.adminTeams) ||
      [];

    if (nestedAdmins.length === 0) {
      result.push(item);
      return;
    }

    nestedAdmins.forEach((admin) => {
      result.push({
        ...item,
        ...admin,
        admin: admin?.admin || admin,
        teamId:
          getMappingTeamId(admin) || getMappingTeamId(item) || item?.teamId,
        teamName: getTeamName(admin) || getTeamName(item),
      });
    });
  });

  return result;
};

const normalizeSearchValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).toLowerCase();
};

const InlineLoader = ({ text = "Loading..." }) => {
  return (
    <div className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{text}</span>
    </div>
  );
};

const EmptyState = ({ title, description }) => {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
      <div className="mb-3 rounded-full bg-white p-3 shadow-sm">
        <Inbox className="h-6 w-6 text-slate-400" />
      </div>
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <p className="mt-1 max-w-md text-xs text-slate-500">{description}</p>
    </div>
  );
};

const AdminDropdown = ({ admins = [], value, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedAdmin = admins.find(
    (admin) => getAdminId(admin) === String(value),
  );

  const filteredAdmins = admins.filter((admin) => {
    const query = search.toLowerCase().trim();

    return [
      getAdminName(admin),
      getAdminId(admin),
      getAdminLoginId(admin),
    ].some((item) =>
      String(item || "")
        .toLowerCase()
        .includes(query),
    );
  });

  return (
    <div
      className="relative w-full"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsOpen(false);
          setSearch("");
        }
      }}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setIsOpen((prev) => !prev);
          setSearch("");
        }}
        className="flex h-[46px] w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 text-sm disabled:opacity-60"
      >
        <span className="truncate">
          {selectedAdmin
            ? `${getAdminName(selectedAdmin)} - ${getAdminLoginId(selectedAdmin) || getAdminId(selectedAdmin)}`
            : "Select admin"}
        </span>
        <span>⌄</span>
      </button>

      {isOpen && !disabled && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                autoFocus
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setIsOpen(false);
                    setSearch("");
                  }
                }}
                placeholder="Search admin..."
                className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-orange-400"
              />
            </div>
          </div>

          <div className="max-h-[264px] overflow-y-auto overscroll-contain">
            {filteredAdmins.length === 0 ? (
              <p className="p-3 text-center text-sm text-slate-400">
                No admin found
              </p>
            ) : (
              filteredAdmins.map((admin) => {
                const adminId = getAdminId(admin);

                return (
                  <button
                    key={adminId}
                    type="button"
                    onClick={() => {
                      onChange(adminId);
                      setIsOpen(false);
                      setSearch("");
                    }}
                    className="block w-full px-4 py-2.5 text-left text-sm hover:bg-orange-50"
                  >
                    {getAdminName(admin)} - {getAdminLoginId(admin) || adminId}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminTeamTab({
  admins = [],
  adminTeams = [],
  adminsLoading = false,
  adminTeamsLoading = false,
  addAdminTeamLoading = false,
  deleteAdminTeamLoading = false,
  onRefreshTeams,
  onAddAdminTeam,
  onDeleteAdminTeam,
}) {
  const [selectedAdminId, setSelectedAdminId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const {
    teams = [],
    teamsLoading = false,
    fetchDriverTeams,
  } = useDriverTeams();

  useEffect(() => {
    fetchDriverTeams();
  }, [fetchDriverTeams]);

  const safeAdmins = useMemo(() => {
    const unique = new Map();

    (Array.isArray(admins) ? admins : []).forEach((admin, index) => {
      if (!admin || typeof admin !== "object") return;

      const adminId = getAdminId(admin);
      const key = adminId || getAdminLoginId(admin) || `admin-${index}`;

      if (!unique.has(key)) {
        unique.set(key, admin);
      }
    });

    return Array.from(unique.values());
  }, [admins]);

  const adminById = useMemo(() => {
    return safeAdmins.reduce((map, admin) => {
      const adminId = getAdminId(admin);
      if (adminId) map[adminId] = admin;
      return map;
    }, {});
  }, [safeAdmins]);

  const teamById = useMemo(() => {
    return (Array.isArray(teams) ? teams : []).reduce((map, team) => {
      const id = String(team?.id ?? team?.teamId ?? team?.team_id ?? "").trim();

      if (id) {
        map[id] = team;
      }

      return map;
    }, {});
  }, [teams]);

  const flatAdminTeams = useMemo(() => {
    return flattenAdminTeams(adminTeams);
  }, [adminTeams]);

  const filteredAdminTeams = useMemo(() => {
    const query = normalizeSearchValue(searchText).trim();

    if (!query) return flatAdminTeams;

    return flatAdminTeams.filter((item) => {
      const adminId = getMappingAdminId(item);
      const mappedAdmin = adminById[adminId];

      const mappingTeamId = getMappingTeamId(item);
      const mappedTeam = teamById[mappingTeamId];

      return [
        adminId,
        mappingTeamId,
        getTeamName(item),
        mappedTeam?.team_name,
        mappedTeam?.teamName,
        mappedAdmin ? getAdminName(mappedAdmin) : "",
        mappedAdmin ? getAdminLoginId(mappedAdmin) : "",
        item?.adminName,
        item?.admin_name,
        item?.first_name,
        item?.last_name,
      ].some((value) => normalizeSearchValue(value).includes(query));
    });
  }, [flatAdminTeams, searchText, adminById, teamById]);

  const handleAdd = async (event) => {
    event.preventDefault();

    const cleanAdminId = String(selectedAdminId || "").trim();
    const cleanTeamId = String(teamId || "").trim();

    if (!cleanAdminId) {
      toast.error("Please select admin");
      return;
    }

    if (!cleanTeamId) {
      toast.error("Please select team");
      return;
    }

    const success = await onAddAdminTeam?.({
      adminId: cleanAdminId,
      teamId: cleanTeamId,
    });

    if (success) {
      setSelectedAdminId("");
      setTeamId("");
    }
  };

  const openDeleteModal = (item) => {
    const adminId = getMappingAdminId(item);
    const mappingTeamId = getMappingTeamId(item);
    const mappingId = String(item?.id ?? item?.pk_admin_team_id ?? "").trim();

    if (!adminId || !mappingTeamId || !mappingId) {
      toast.error("Admin ID, Team ID or Mapping ID missing in API response");
      return;
    }

    const mappedAdmin = adminById[adminId];
    const mappedTeam = teamById[mappingTeamId];
    const directName =
      item?.adminName ||
      item?.admin_name ||
      `${item?.first_name || ""} ${item?.last_name || ""}`.trim();

    setDeleteTarget({
      item,
      adminId,
      teamId: mappingTeamId,
      mappingId,
      adminName: mappedAdmin
        ? getAdminName(mappedAdmin)
        : directName || "Admin",
      loginId: mappedAdmin
        ? getAdminLoginId(mappedAdmin)
        : String(item?.admin_loginid || item?.loginId || ""),
      teamName:
        getTeamName(item) ||
        mappedTeam?.team_name ||
        mappedTeam?.teamName ||
        "Team",
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.adminId || !deleteTarget?.mappingId) return;

    const success = await onDeleteAdminTeam?.({
      adminId: deleteTarget.adminId,
      // API expects teaList[].id in the teamId field for delete.
      teamId: deleteTarget.mappingId,
    });

    if (success !== false) {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="grid h-full min-h-0 min-w-0 grid-rows-[auto_minmax(0,1fr)] gap-4 overflow-hidden">
      <div className="shrink-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-orange-50 p-2.5 text-orange-600">
              <UsersRound className="h-5 w-5" />
            </div>

            <div>
              <h2 className="text-base font-bold text-slate-900">Admin Team</h2>
            </div>
          </div>

          <button
            type="button"
            onClick={onRefreshTeams}
            disabled={adminTeamsLoading}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              className={`h-4 w-4 ${adminTeamsLoading ? "animate-spin" : ""}`}
            />
            Refresh Teams
          </button>
        </div>

        <form
          onSubmit={handleAdd}
          className="mt-4 grid gap-3 md:grid-cols-[minmax(260px,1fr)_minmax(180px,0.55fr)_auto] md:items-end"
        >
          <div>
            <label className="mb-1.5 block text-xs font-bold text-slate-600">
              Admin To Add
            </label>

            <AdminDropdown
              admins={safeAdmins}
              value={selectedAdminId}
              onChange={setSelectedAdminId}
              disabled={adminsLoading || addAdminTeamLoading}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-bold text-slate-600">
              Team
            </label>

            <TeamDropdown
              teams={teams}
              value={teamId}
              onChange={setTeamId}
              placeholder="Select Team"
              loading={teamsLoading || addAdminTeamLoading}
              dropdownHeight="max-h-60"
            />
          </div>

          <button
            type="submit"
            disabled={addAdminTeamLoading || adminsLoading || teamsLoading}
            className="inline-flex h-[46px] items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {addAdminTeamLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="h-4 w-4" />
            )}
            {addAdminTeamLoading ? "Adding..." : "Add Admin"}
          </button>
        </form>
      </div>

      <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex shrink-0 flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900">
              Admin Team Mappings
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Total mappings: {flatAdminTeams.length}
            </p>
          </div>

          <div className="relative w-full lg:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search admin, login ID, Team ID or team name..."
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          {adminTeamsLoading ? (
            <div className="h-full overflow-y-auto p-4">
              <InlineLoader text="Loading admin teams..." />
            </div>
          ) : filteredAdminTeams.length === 0 ? (
            <div className="h-full overflow-y-auto p-4">
              <EmptyState
                title={
                  searchText
                    ? "No matching team mapping"
                    : "No admin teams found"
                }
                description={
                  searchText
                    ? "Try another admin, Team ID or team name."
                    : "Use the form above to add an admin to a team."
                }
              />
            </div>
          ) : (
            <div className="h-full w-full overflow-auto overscroll-contain">
              <table className="w-full min-w-[820px] divide-y divide-slate-200 text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50 shadow-sm">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      Admin
                    </th>
                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      Admin ID
                    </th>
                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      Team
                    </th>
                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      Team ID
                    </th>
                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-slate-500">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 bg-white">
                  {filteredAdminTeams.map((item, index) => {
                    const adminId = getMappingAdminId(item);
                    const mappingTeamId = getMappingTeamId(item);
                    const mappedAdmin = adminById[adminId];
                    const directName =
                      item?.adminName ||
                      item?.admin_name ||
                      `${item?.first_name || ""} ${item?.last_name || ""}`.trim();
                    const adminName = mappedAdmin
                      ? getAdminName(mappedAdmin)
                      : directName || "Admin";
                    const loginId = mappedAdmin
                      ? getAdminLoginId(mappedAdmin)
                      : item?.admin_loginid || item?.loginId || "";
                    const mappedTeam = teamById[mappingTeamId];
                    const teamName =
                      getTeamName(item) ||
                      mappedTeam?.team_name ||
                      mappedTeam?.teamName ||
                      "";
                    const rowKey =
                      item?.id ||
                      item?.pk_admin_team_id ||
                      `${adminId || "admin"}-${mappingTeamId || "team"}-${index}`;
                    const canDelete = Boolean(
                      adminId &&
                      mappingTeamId &&
                      (item?.id ?? item?.pk_admin_team_id),
                    );

                    return (
                      <tr key={rowKey} className="hover:bg-slate-50">
                        <td className="whitespace-nowrap px-4 py-3">
                          <p className="font-bold text-slate-900">
                            {adminName}
                          </p>
                          {loginId ? (
                            <p className="mt-0.5 text-xs text-slate-400">
                              Login ID: {loginId}
                            </p>
                          ) : null}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-600">
                          {adminId || "N/A"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700">
                            {teamName || "Team"}
                          </span>
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-600">
                          {mappingTeamId || "N/A"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          <button
                            type="button"
                            disabled={deleteAdminTeamLoading || !canDelete}
                            onClick={() => openDeleteModal(item)}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-50 px-4 py-2 text-xs font-bold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {deleteAdminTeamLoading ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {deleteTarget ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[2px]"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              !deleteAdminTeamLoading
            ) {
              setDeleteTarget(null);
            }
          }}
        >
          <div className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-rose-50 p-2.5 text-rose-600">
                  <AlertTriangle className="h-5 w-5" />
                </div>

                <div>
                  <h3 className="text-base font-black text-slate-900">
                    Remove Admin From Team?
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Please verify the mapping details before deleting it.
                  </p>
                </div>
              </div>

              <button
                type="button"
                disabled={deleteAdminTeamLoading}
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Close delete confirmation"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 p-5">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-3 text-sm">
                  <span className="font-semibold text-slate-500">Admin</span>
                  <div className="min-w-0">
                    <p className="truncate font-bold text-slate-900">
                      {deleteTarget.adminName}
                    </p>
                    {deleteTarget.loginId ? (
                      <p className="mt-0.5 text-xs text-slate-400">
                        Login ID: {deleteTarget.loginId}
                      </p>
                    ) : null}
                  </div>

                  <span className="font-semibold text-slate-500">Admin ID</span>
                  <span className="font-bold text-slate-800">
                    {deleteTarget.adminId}
                  </span>

                  <span className="font-semibold text-slate-500">Team</span>
                  <span className="font-bold text-slate-800">
                    {deleteTarget.teamName}
                  </span>

                  <span className="font-semibold text-slate-500">Team ID</span>
                  <span className="font-bold text-slate-800">
                    {deleteTarget.teamId}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/70 px-5 py-4">
              <button
                type="button"
                disabled={deleteAdminTeamLoading}
                onClick={() => setDeleteTarget(null)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={deleteAdminTeamLoading}
                onClick={handleConfirmDelete}
                className="inline-flex min-w-[132px] items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deleteAdminTeamLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {deleteAdminTeamLoading ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
